import { useState, useEffect, useCallback } from 'react';
import { customFieldsAPI } from '../utils/api';
import { FormField } from './layout/UI';
import { useToast, apiError } from '../components/Toast';

/**
 * Renders admin-defined custom fields and edits the values JSONB column.
 * Usage: <CustomFieldsEditor appliesTo="task" projectTypeId={task.project_type_id}
 *          values={task.custom_fields} onChange={(newValues) => ...} />
 */
export default function CustomFieldsEditor({ appliesTo, projectTypeId, values = {}, onChange }) {
  const toast = useToast();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await customFieldsAPI.getAll(appliesTo, projectTypeId);
      setFields(r.data);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); } finally { setLoading(false); }
  }, [appliesTo, projectTypeId]);

  useEffect(() => { load(); }, [load]);

  if (loading || fields.length === 0) return null;

  const setVal = (key, val) => onChange({ ...values, [key]: val });

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
      <p className="text-xs font-bold uppercase tracking-wider" style={{ color:'#2E539F' }}>Additional Fields</p>
      <div className="grid grid-cols-2 gap-4">
        {fields.map(f => (
          <FormField key={f.id} label={f.label} required={f.is_required}>
            {f.field_type === 'select' ? (
              <select className="select" value={values[f.field_key]||''} onChange={e=>setVal(f.field_key, e.target.value)}>
                <option value="">Select...</option>
                {(f.options||[]).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.field_type === 'checkbox' ? (
              <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color:'var(--text)' }}>
                <input type="checkbox" checked={!!values[f.field_key]} onChange={e=>setVal(f.field_key, e.target.checked)}/> Yes
              </label>
            ) : f.field_type === 'textarea' ? (
              <textarea className="input" rows={2} value={values[f.field_key]||''} onChange={e=>setVal(f.field_key, e.target.value)}/>
            ) : (
              <input type={f.field_type==='date'?'date':f.field_type==='number'?'number':'text'}
                className="input" value={values[f.field_key]||''} onChange={e=>setVal(f.field_key, e.target.value)}/>
            )}
          </FormField>
        ))}
      </div>
    </div>
  );
}
